import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { PORT_CONFIG_OPTIONS } from '../../../utils/portConfigs';

export type SerialPortConfig = {
  type: typeof PORT_CONFIG_OPTIONS.Serial;
  portName: string;
  baudRate: number;
  dataBits: number;
  stopBits: StopBitsName;
  parity: string;
};

export type TcpPortConfig = {
  type: typeof PORT_CONFIG_OPTIONS.TcpIp;
  host: string;
  port: number;
};

export type MockPortConfig = {
  type: typeof PORT_CONFIG_OPTIONS.Mock;
};

export type HttpPortConfig = {
  type: typeof PORT_CONFIG_OPTIONS.Http;
  host: string;
  /** 0 for the scheme's default port. */
  port: number;
  useHttps: boolean;
  username: string;
  password: string;
};

export type PortConfig = SerialPortConfig | TcpPortConfig | MockPortConfig | HttpPortConfig;

type PortType = (typeof PORT_CONFIG_OPTIONS)[keyof typeof PORT_CONFIG_OPTIONS];

type PortConfigProps = {
  value: PortConfig;
  onChange: (next: PortConfig) => void;
  disabled?: boolean;
  /** Defaults to Serial, TCP and Mock; HTTP is only for web relay boards, so it must be listed. */
  allowedTypes?: PortType[];
};

const DEFAULT_TYPES: PortType[] = [PORT_CONFIG_OPTIONS.Serial, PORT_CONFIG_OPTIONS.TcpIp, PORT_CONFIG_OPTIONS.Mock];

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

const BAUD_RATES = [1200, 2400, 4800, 9600, 19200, 38400, 57600, 115200];
const DATA_BITS = [5, 6, 7, 8];
const PARITIES = ['None', 'Odd', 'Even', 'Mark', 'Space'];

const PORT_TYPE_BY_NUMBER: Record<number, string> = {
  0: PORT_CONFIG_OPTIONS.Serial,
  1: PORT_CONFIG_OPTIONS.TcpIp
};

// System.IO.Ports.StopBits names. Saved by name so the device reads them with JsonStringEnumConverter:
// by number, 1.5 is not an enum value at all and would fail the whole configuration.
const STOP_BITS = [
  { name: 'One', label: '1' },
  { name: 'OnePointFive', label: '1.5' },
  { name: 'Two', label: '2' }
] as const;

type StopBitsName = (typeof STOP_BITS)[number]['name'];

// Numbers seen in saved configurations: 1, 1.5 and 2 from this editor before it saved names, and 3 for
// OnePointFive from C# when enums are written by number.
const STOP_BITS_BY_NUMBER: Record<number, StopBitsName> = {
  1: 'One',
  1.5: 'OnePointFive',
  2: 'Two',
  3: 'OnePointFive'
};

function resolvePortType(raw: unknown): string {
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'number') return PORT_TYPE_BY_NUMBER[raw] ?? PORT_CONFIG_OPTIONS.Serial;
  return PORT_CONFIG_OPTIONS.Serial;
}

function resolveStopBits(raw: unknown): StopBitsName {
  if (typeof raw === 'string') {
    const byName = STOP_BITS.find((s) => s.name.toLowerCase() === raw.trim().toLowerCase());
    return byName?.name ?? 'One';
  }
  return STOP_BITS_BY_NUMBER[asNumber(raw, 1)] ?? 'One';
}

/**
 * Reads a field written in camelCase (this editor) or PascalCase (the Local API, which saves the
 * indicator definition as C# serialises it).
 */
function getField(record: Record<string, unknown>, camel: string): unknown {
  return record[camel] !== undefined ? record[camel] : record[camel.charAt(0).toUpperCase() + camel.slice(1)];
}

export function buildDefaultPortConfig(base?: unknown): PortConfig {
  const source = asRecord(base);
  const type = resolvePortType(getField(source, 'type'));

  if (type === PORT_CONFIG_OPTIONS.TcpIp) {
    return {
      type: PORT_CONFIG_OPTIONS.TcpIp,
      host: asString(getField(source, 'host'), '127.0.0.1'),
      port: asNumber(getField(source, 'port'), 5000)
    };
  }

  if (type === PORT_CONFIG_OPTIONS.Mock) {
    return { type: PORT_CONFIG_OPTIONS.Mock };
  }

  if (type === PORT_CONFIG_OPTIONS.Http) {
    return {
      type: PORT_CONFIG_OPTIONS.Http,
      host: asString(getField(source, 'host')),
      port: asNumber(getField(source, 'port'), 0),
      useHttps: getField(source, 'useHttps') === true,
      username: asString(getField(source, 'username')),
      password: asString(getField(source, 'password'))
    };
  }

  return {
    type: PORT_CONFIG_OPTIONS.Serial,
    portName: asString(getField(source, 'portName'), 'COM1'),
    baudRate: asNumber(getField(source, 'baudRate'), 9600),
    dataBits: asNumber(getField(source, 'dataBits'), 8),
    stopBits: resolveStopBits(getField(source, 'stopBits')),
    parity: asString(getField(source, 'parity'), 'None')
  };
}

export default function PortConfig({ value, onChange, disabled = false, allowedTypes = DEFAULT_TYPES }: PortConfigProps) {
  const handleTypeChange = (type: PortType) => {
    if (type === value.type) return;
    onChange(buildDefaultPortConfig({ type }));
  };

  return (
    <Grid size={{ xs: 10 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid size={{ xs: 6, md: 3 }}>
          <TextField
            fullWidth
            select
            label="Port Type"
            value={value.type}
            onChange={(e) => handleTypeChange(e.target.value as PortType)}
            disabled={disabled}
          >
            {allowedTypes.includes(PORT_CONFIG_OPTIONS.Serial) && <MenuItem value={PORT_CONFIG_OPTIONS.Serial}>Serial Port</MenuItem>}
            {allowedTypes.includes(PORT_CONFIG_OPTIONS.TcpIp) && <MenuItem value={PORT_CONFIG_OPTIONS.TcpIp}>TCP Port</MenuItem>}
            {allowedTypes.includes(PORT_CONFIG_OPTIONS.Http) && <MenuItem value={PORT_CONFIG_OPTIONS.Http}>HTTP (web relay)</MenuItem>}
            {allowedTypes.includes(PORT_CONFIG_OPTIONS.Mock) && <MenuItem value={PORT_CONFIG_OPTIONS.Mock}>Mock</MenuItem>}
          </TextField>
        </Grid>

        {value.type === PORT_CONFIG_OPTIONS.Serial ? (
          <>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth
                label="Port Name"
                placeholder="COM1"
                value={value.portName}
                onChange={(e) => onChange({ ...value, portName: e.target.value })}
                disabled={disabled}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth
                select
                label="Baud Rate"
                value={value.baudRate}
                onChange={(e) => onChange({ ...value, baudRate: Number(e.target.value) })}
                disabled={disabled}
              >
                {BAUD_RATES.map((rate) => (
                  <MenuItem key={rate} value={rate}>
                    {rate}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 1.5 }}>
              <TextField
                fullWidth
                select
                label="Data Bits"
                value={value.dataBits}
                onChange={(e) => onChange({ ...value, dataBits: Number(e.target.value) })}
                disabled={disabled}
              >
                {DATA_BITS.map((bits) => (
                  <MenuItem key={bits} value={bits}>
                    {bits}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 1.5 }}>
              <TextField
                fullWidth
                select
                label="Stop Bits"
                value={value.stopBits}
                onChange={(e) => onChange({ ...value, stopBits: e.target.value as StopBitsName })}
                disabled={disabled}
              >
                {STOP_BITS.map((bits) => (
                  <MenuItem key={bits.name} value={bits.name}>
                    {bits.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth
                select
                label="Parity"
                value={value.parity}
                onChange={(e) => onChange({ ...value, parity: e.target.value })}
                disabled={disabled}
              >
                {PARITIES.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </>
        ) : value.type === PORT_CONFIG_OPTIONS.TcpIp ? (
          <>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Host"
                placeholder="192.168.1.100"
                value={value.host}
                onChange={(e) => onChange({ ...value, host: e.target.value })}
                disabled={disabled}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth
                type="number"
                label="Port"
                value={value.port}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  if (Number.isFinite(n)) onChange({ ...value, port: n });
                }}
                disabled={disabled}
              />
            </Grid>
          </>
        ) : value.type === PORT_CONFIG_OPTIONS.Http ? (
          <>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Host"
                placeholder="192.168.1.199"
                value={value.host}
                onChange={(e) => onChange({ ...value, host: e.target.value.trim() })}
                disabled={disabled}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 1.5 }}>
              <TextField
                fullWidth
                type="number"
                label="Port (0 = default)"
                value={value.port}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  if (Number.isInteger(n) && n >= 0) onChange({ ...value, port: n });
                }}
                disabled={disabled}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 1.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={value.useHttps}
                    onChange={(_, checked) => onChange({ ...value, useHttps: checked })}
                    disabled={disabled}
                  />
                }
                label="HTTPS"
              />
            </Grid>
            {/* Grouped so the login always wraps as a pair rather than leaving the password on a line of its own. */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <TextField
                    fullWidth
                    label="Username"
                    autoComplete="off"
                    value={value.username}
                    onChange={(e) => onChange({ ...value, username: e.target.value })}
                    disabled={disabled}
                  />
                </Grid>
                <Grid size={6}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Password"
                    autoComplete="new-password"
                    value={value.password}
                    onChange={(e) => onChange({ ...value, password: e.target.value })}
                    disabled={disabled}
                  />
                </Grid>
              </Grid>
            </Grid>
          </>
        ) : (
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography></Typography>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
}
